// Referrals — the tracked share link, and attributing a signup that came
// through one.
//
// The Share button used to share window.location.href. On a profile page that
// is /profile/view/:userId, a route behind auth: a friend opening it hit a
// login wall, and nothing recorded who sent them. It now shares the site root
// carrying the sharer's code, which is both publicly reachable and the page
// that serves the link-preview metadata.

import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import { logger } from '../../utils/logger';

const API_BASE = `${config.api.baseUrl}/users`;

// Where a pending code waits between opening the link and the account
// existing. localStorage rather than a cookie so it survives the sign-up
// round trip and any OAuth redirect.
const PENDING_CODE_KEY = 'poh.referral.pending';
const PENDING_SOURCE_KEY = 'poh.referral.source';

export interface MyReferral {
  code: string | null;
  link: string | null;
  signups: number;
  firstName: string | null;
}

function headers(json = true): HeadersInit {
  const h: HeadersInit = { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/**
 * Reads ?ref= off the current URL and remembers it.
 *
 * Called on app start, before anyone has signed in. The code is kept until an
 * account exists to attach it to, which is what makes attribution work for
 * someone who opens the link, browses, and signs up later.
 */
export function captureReferralFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (!code) return;

    localStorage.setItem(PENDING_CODE_KEY, code.trim().toUpperCase());
    localStorage.setItem(PENDING_SOURCE_KEY, params.get('src') || 'share_link');
    logger.log('[REFERRAL] Captured referral code from link');

    // Strip it from the address bar so it does not get re-shared or bookmarked
    // as if it were the visitor's own code, without adding a history entry.
    params.delete('ref');
    params.delete('src');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
  } catch (error) {
    // A blocked localStorage (private mode, embedded webview) must not stop
    // the app from loading.
    logger.log('[REFERRAL] Could not capture referral code:', error);
  }
}

export function getPendingReferralCode(): string | null {
  try {
    return localStorage.getItem(PENDING_CODE_KEY);
  } catch {
    return null;
  }
}

function clearPendingReferral(): void {
  try {
    localStorage.removeItem(PENDING_CODE_KEY);
    localStorage.removeItem(PENDING_SOURCE_KEY);
  } catch {
    /* nothing to clear */
  }
}

class ReferralService {
  /** The signed-in user's code, share link and signup count. */
  async getMyReferral(): Promise<MyReferral> {
    const response = await fetch(`${API_BASE}/referral`, { headers: headers(false) });
    if (!response.ok) throw new Error(`Failed to load referral link: ${response.status}`);
    const { data } = await response.json();
    return data;
  }

  /**
   * Attributes the signed-in user to a pending code, if there is one.
   *
   * Safe to call on every sign-in: the backend treats an already-attributed
   * member as settled rather than an error, and the pending code is cleared
   * either way so a stale one cannot follow someone around forever.
   */
  async claimPendingReferral(): Promise<void> {
    const code = getPendingReferralCode();
    if (!code) return;

    try {
      const response = await fetch(`${API_BASE}/referral/claim`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          code,
          source: (() => {
            try { return localStorage.getItem(PENDING_SOURCE_KEY) || undefined; } catch { return undefined; }
          })(),
        }),
      });

      if (!response.ok) {
        // Leave the code in place: a server error may succeed on the next
        // sign-in, and dropping it would lose the attribution for good.
        logger.log(`[REFERRAL] Claim failed (${response.status}), keeping code for retry`);
        return;
      }

      const { data } = await response.json();
      logger.log(
        data?.attributed
          ? '[REFERRAL] Signup attributed to referrer'
          : `[REFERRAL] Not attributed: ${data?.reason}`,
      );
      // Cleared on any definite answer, including a refusal — retrying an
      // "already attributed" or "unknown code" result never changes it.
      clearPendingReferral();
    } catch (error) {
      logger.log('[REFERRAL] Claim error, keeping code for retry:', error);
    }
  }

  /**
   * The payload for the native share sheet. Falls back to the bare site URL if
   * the code cannot be loaded, so Share still does something useful.
   */
  async buildShareData(firstName?: string | null): Promise<{ title: string; text: string; url: string }> {
    let url = 'https://plentyofhearts.com';
    try {
      const referral = await this.getMyReferral();
      if (referral.link) url = referral.link;
    } catch (error) {
      logger.log('[REFERRAL] Falling back to an untracked share link:', error);
    }

    const name = firstName?.trim();
    return {
      title: 'Plenty of Hearts',
      text: name
        ? `${name} invited you to Plenty of Hearts — play games, plan dates, and meet people who share your interests.`
        : 'Join me on Plenty of Hearts — play games, plan dates, and meet people who share your interests.',
      url,
    };
  }
}

export const referralService = new ReferralService();
